// @flow

import Features from './homes/Features';
import IncludedServices from './homes/IncludedServices';
import Schedule from './homes/Schedule';
import Amenities from './homes/Amenities';
import HomeFeatures from 'models/HomeFeatures';
import Characteristics from './homes/Characteristics';
import PhotoShooting from './homes/Photoshooting';
import Spaces from './homes/Spaces';
import _ from 'lodash';
import moment from 'moment';
import CurrentUser from 'models/CurrentUser';
import { pluralize } from 'utils/Misc';
import { IconsMeero } from 'utils/ExportCommon';
import { formatCurrency } from 'utils/Misc';

export default class Home {
  id: string;
  slug: string;
  name: string;
  description: string;
  abbrAddress: string;
  totalAmount: number;
  monthlyAmount: number;
  currency: string;
  photos: Array<string>;
  characteristics: Characteristics;
  features: Features;
  schedule: Array<Schedule>;
  amenities: Amenities;
  spaces: Spaces;
  interestedPeopleFake: number;
  depositMonthsWarranty: number;
  price: number;
  includedServices: IncludedServices;
  extraServicesAmount: number;
  owner: CurrentUser;
  availableFrom: Object;
  availableForMeetingsFrom: Object;
  location: {
    lat?: number,
    lng?: number
  };
  one_line_address: string;
  banner_photo: string;
  favorite_id: string;
  user_interested: boolean;
  apartment_type: string;
  street: string;
  number: string;
  city: string;
  state: string;
  neighborhood: string;
  int_number: string;
  antique: number;
  apartment_floor: string;
  sqare_mts: number;
  zip: string;
  status: string;
  recommended_number_of_tenants: number;
  home_features: HomeFeatures;
  bathrooms: number;
  bedrooms: number;
  parkings: number;
  floor: number;
  minimal_rent_months: number;
  pets_deposit_amount: number;
  is_rent_billable: boolean;
  terrace_sqare_mts: number;
  balcony_sqare_mts: number;
  garden_sqare_mts: number;
  yard_sqare_mts: number;
  visits: number;
  homeMeetingsNumber: number;
  reservation_price: number;
  home_schedule: any;
  assets: Array;
  identifications: Array;
  petFriendlyShow: boolean;
  youtubeLink: string;
  virtualTourLink: string;
  photoShooting: PhotoShooting;
  isHomieExclusive: boolean;
  isApartAllowed: boolean;
  rentsCount: number;
  isUnderMaintenance: boolean;
  pets_allowed: number;
  isFromProductSeeker: boolean;
  is_showed_by_homie: boolean;
  isCandidateProduct_100_550: boolean;
  accepted_terms_and_conditions: boolean;

  constructor(data: any = {}) {
    moment.locale('es');
    this.id = data.id;
    this.slug = data.slug;
    this.name = data.name;
    this.isHomieExclusive = data.is_homie_exclusive || false;
    this.isApartAllowed = !!data.is_apart_allowed;
    this.description = data.description;
    this.abbrAddress = data.abbr_address || '';
    this.totalAmount = data.total_amount;
    this.monthlyAmount = data.monthly_amount || 0;
    this.currency = data.currency;
    this.characteristics = new Characteristics(data.home_features);
    this.features = data.features
      ? new Features(data.features)
      : new Features({
          square_mts: data.sqare_mts,
          bedrooms: data.bedrooms,
          parkings: data.parkings,
          bathrooms: data.bathrooms
        });
    this.includedServices = new IncludedServices(data.home_features);
    this.amenities = new Amenities(data.home_features);
    this.home_features = new HomeFeatures(data.home_features);
    this.spaces = new Spaces(data.home_features);
    this.owner = data.owner ? data.owner.user : new CurrentUser();
    this.home_schedule = data.home_schedule;
    this.schedule = Home.buildSchedule(
      (data.home_schedule || {}).schedule_globals
    );
    this.location = data.location || {};
    this.interestedPeopleFake = data.interested_people_fake;
    this.depositMonthsWarranty = data.deposit_months_warranty;
    this.price = data.price;
    this.extraServicesAmount = data.extra_services_amount;
    this.availableFrom = moment(data.available_from);
    this.availableForMeetingsFrom = moment(data.available_for_meetings_from);
    this.one_line_address = data.one_line_address || '';
    this.banner_photo = data.banner_photo || '';
    this.favorite_id = data.favorite_id;
    this.apartment_type = data.apartment_type;
    this.street = data.street || '';
    this.number = data.number || '';
    this.city = data.city || '';
    this.state = data.state || '';
    this.neighborhood = data.neighborhood || '';
    this.int_number = data.int_number || '';
    this.antique = data.antique || 0;
    this.apartment_floor = data.apartment_floor || '';
    this.sqare_mts = data.sqare_mts || this.features.squareMts;
    this.bedrooms = data.bedrooms || this.features.bedrooms;
    this.bathrooms = data.bathrooms || this.features.bathrooms;
    this.parkings = data.parkings || this.features.parkings;
    this.zip = data.zip || '';
    this.status = data.status || '';
    this.user_interested = data['user_interested?'];
    this.recommended_number_of_tenants =
      data.recommended_number_of_tenants || 0;
    this.floor = data.floor || 0;
    this.minimal_rent_months = data.minimal_rent_months || 0;
    this.pets_deposit_amount = data.pets_deposit_amount || 0;
    this.is_rent_billable = data.is_rent_billable || false;
    this.terrace_sqare_mts = data.terrace_sqare_mts || 0;
    this.balcony_sqare_mts = data.balcony_sqare_mts || 0;
    this.garden_sqare_mts = data.garden_sqare_mts || 0;
    this.yard_sqare_mts = data.yard_sqare_mts || 0;
    this.visits = data.visits || 0;
    this.homeMeetingsNumber = data.home_meetings_number || 0;
    this.reservation_price = data.reservation_price || 0;
    this.assets = data.assets || [];
    this.identifications = data.identifications || [];
    this.availableAgainFrom =
      moment(data.available_again_from).format('DD [de] MMMM [del] YYYY') || '';
    this.petFriendlyShow = data.home_features
      ? data.home_features.pet_friendly
        ? true
        : false
      : false;
    this.youtubeLink = data.youtube_link || '';
    this.virtualTourLink = data.virtual_tour_link || '';
    this.photoShooting = new PhotoShooting(
      !_.isNil(data.photoshooting) && data.photoshooting
    );
    this.photos = data.photos || [];
    this.rentsCount = data.rents_count || 0;
    this.isUnderMaintenance = data.is_under_maintenance;
    this.pets_allowed = data.pets_allowed || 0;
    this.isFromProductSeeker = !!data.is_from_product_seeker;
    this.is_showed_by_homie = data.is_showed_by_homie;
    this.isCandidateProduct_100_550 = data.is_candidate_product_100_550;
    this.accepted_terms_and_conditions = data.accepted_terms_and_conditions;
  }

  static buildSchedule(data: any): Array<Schedule> {
    if (_.isEmpty(data)) {
      return [];
    }

    const dates = {};
    const scheduleData = data.reduce(function(map, obj) {
      if (obj['date']) {
        map[obj.day] = obj;
        let date = obj.date.split('-').join('');
        dates[date] = obj.date;
      }

      return map;
    }, {});

    let keys = Object.keys(dates);
    let key = Math.min(...keys);
    let closestDate = dates[key];

    const schedule = [];
    const currentDate = moment(closestDate);

    for (let extraDays = 0; extraDays < 7; extraDays++) {
      let calculatedDate = currentDate.clone().add(extraDays, 'days');
      const weekDay = calculatedDate.format('dddd');
      const dayOfWeek = calculatedDate.format('d');
      const day = calculatedDate.format('D');
      const month = calculatedDate.format('MMMM');

      if (scheduleData[dayOfWeek]) {
        const available_times = _.sortBy(
          scheduleData['' + dayOfWeek].available_times
        );
        if (available_times.length) {
          const minHour = Math.min(...available_times);
          const maxHour = Math.max(...available_times);
          const period = [minHour, maxHour];

          const hourTwoDigits = minHour.toString().padStart(2, '0');
          const fullHour = `${hourTwoDigits}:00:00`;

          schedule.push(
            new Schedule({
              weekDay: weekDay.replace(/\b\w/, l => l.toUpperCase()),
              day: day,
              month: month.replace(/\b\w/, l => l.toUpperCase()),
              period: period,
              backendFormattedDate: calculatedDate.format('DD-MM-YYYY'),
              backendFormattedTime: fullHour,
              availableTimes: available_times
            })
          );
        }
      }
    }

    return schedule;
  }

  static get featureImages() {
    return {
      bedrooms: '/assets/homie_v3/homes/features/service_room.svg',
      bathrooms: '/assets/homie_v3/homes/features/Bathrooms.svg',
      parkings: '/assets/homie_v3/homes/features/visits_space.svg',
      petFriendly: '/assets/homie_v3/homes/features/pet.png',
      squareMts: '/assets/homie_v3/homes/features/square_mts.png',
      apartment: '/assets/homie_v3/homes/features/apartment.svg'
    };
  }

  getRealPhotos = (
    photos: Array,
    hasPhotoShooting: boolean,
    bannerPhoto: string
  ) => {
    if (hasPhotoShooting && photos.length <= 0) {
      if (bannerPhoto) return [bannerPhoto];
      return [IconsMeero.DefaultImage];
    }
    return photos;
  };

  get formatedTotalAmount() {
    return this.__formatCurrency(this.totalAmount);
  }

  get formatedPrice() {
    return formatCurrency(this.price, 2);
  }

  get formatedMonthlyAmount() {
    return formatCurrency(this.monthlyAmount, 2);
  }

  get formatedExtraServicesAmount() {
    return formatCurrency(this.extraServicesAmount, 2);
  }

  get homeCharacteristics() {
    return _.keys(
      _.pickBy(this.characteristics, hasCharacteristic => hasCharacteristic)
    );
  }

  get homeAmenities() {
    return _.keys(_.pickBy(this.amenities, hasAmenity => hasAmenity));
  }

  get homeSpaces() {
    return _.keys(_.pickBy(this.spaces, hasSpace => hasSpace));
  }

  featuresHash = () => {
    return {
      bedrooms: {
        text: `${this.bedrooms} ${pluralize(this.bedrooms, 'cuarto')}`,
        functionValue: 'bedrooms',
        icon: {
          iconDark: Home.featureImages.bedrooms
        }
      },
      bathrooms: {
        text: `${this.bathrooms} ${pluralize(this.bathrooms, 'baño')}`,
        functionValue: 'bathrooms',
        icon: {
          iconDark: Home.featureImages.bathrooms
        }
      },
      parkings: {
        text: `${this.parkings} ${pluralize(this.parkings, 'espacio')}`,
        functionValue: 'parkings',
        icon: {
          iconDark: Home.featureImages.parkings
        }
      },
      petFriendly: {
        text: this.home_features.pet_friendly ? 'Acepta' : 'No aceptan',
        functionValue: 'petFriendly',
        icon: {
          iconDark: Home.featureImages.petFriendly
        }
      }
    };
  };
  // Private

  __formatCurrency = (quantity: number) => {
    if (quantity) {
      return `$${quantity.toLocaleString(undefined, {
        maximumFractionDigits: 2
      })}`;
    }
    return '$0.00';
  };
}
